import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

const PaginatedTable = ({
  columns,
  data,
  renderRow,
  emptyMessage,
  sortType = 'string',
  title,
  containerClassName = '',
  tableClassName = '',
  wrapperClassName = '',
  sortableHeaderClassName = '',
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  resetPageKey
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: columns[0]?.key || '',
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(rowsPerPageOptions[0] || 10);

  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      if (sortType === 'numeric') {
        if (sortConfig.direction === 'asc') {
          return a[sortConfig.key] - b[sortConfig.key];
        }
        return b[sortConfig.key] - a[sortConfig.key];
      }
      const aVal = String(a[sortConfig.key] || '').toLowerCase();
      const bVal = String(b[sortConfig.key] || '').toLowerCase();
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [data, sortConfig, sortType]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Reset pagination settings when parent context changes (e.g., leaderboard tab switch).
  useEffect(() => {
    setCurrentPage(1);
    setItemsPerPage(rowsPerPageOptions[0] || 10);
    setSortConfig({
      key: columns[0]?.key || '',
      direction: 'asc'
    });
  }, [resetPageKey, rowsPerPageOptions, columns]);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return 'unfold_more';
    }
    return sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward';
  };

  return (
    <div className={containerClassName}>
      {title}
      <div className={wrapperClassName}>
        <table className={tableClassName}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className={sortableHeaderClassName}>
                  <div className='flex items-center gap-1'>
                    {col.label}{' '}
                    <span className='material-icons text-[16px]'>
                      {getSortIcon(col.key)}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row, idx) => renderRow(row, idx))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className='py-6 text-center text-[14px] text-[#DADADA]'>
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {sortedData.length > itemsPerPage && (
        <div className='flex items-center justify-between px-4 py-3'>
          <div className='flex items-center gap-2'>
            <span className='text-[14px] text-[#DADADA]'>Rows per page</span>
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className='rounded border border-[#4E4E4E] bg-[#2a2a2a] px-2 py-1 text-[14px] text-[#DADADA] outline-none focus:border-[#0066FF]'>
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className='flex items-center gap-3'>
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className='material-icons text-[20px] text-[#DADADA] disabled:cursor-not-allowed disabled:opacity-30'
              aria-label='First page'>
              first_page
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className='material-icons text-[20px] text-[#DADADA] disabled:cursor-not-allowed disabled:opacity-30'
              aria-label='Previous page'>
              chevron_left
            </button>
            <div className='flex items-center gap-2'>
              <span className='text-[14px] text-[#DADADA]'>Page</span>
              <input
                type='number'
                value={currentPage}
                onChange={(e) => handlePageChange(Number(e.target.value))}
                min={1}
                max={totalPages}
                className='w-12 rounded border border-[#4E4E4E] bg-[#2a2a2a] px-2 py-1 text-center text-[14px] text-[#DADADA] outline-none focus:border-[#0066FF]'
              />
              <span className='text-[14px] text-[#DADADA]'>
                of {totalPages}
              </span>
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className='material-icons text-[20px] text-[#DADADA] disabled:cursor-not-allowed disabled:opacity-30'
              aria-label='Next page'>
              chevron_right
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className='material-icons text-[20px] text-[#DADADA] disabled:cursor-not-allowed disabled:opacity-30'
              aria-label='Last page'>
              last_page
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

PaginatedTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  renderRow: PropTypes.func.isRequired,
  emptyMessage: PropTypes.string.isRequired,
  sortType: PropTypes.oneOf(['string', 'numeric']),
  title: PropTypes.node,
  containerClassName: PropTypes.string,
  tableClassName: PropTypes.string,
  wrapperClassName: PropTypes.string,
  sortableHeaderClassName: PropTypes.string,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  resetPageKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default React.memo(PaginatedTable);
